/**
 * API DIFFERENCES vs ../progress.tsx (shadcn):
 * - Variants: linear|circular vs linear only
 * - Size: sm|md|lg vs fixed h-2
 * - Composition: Progress + ProgressTrack + ProgressIndicator + ProgressLabel + ProgressValue
 *   vs single Progress with barClassName
 * - Indeterminate: value=null with animation vs no indeterminate support
 */
'use client'

import type { CSSProperties } from 'react'
import { createContext, useContext, useMemo } from 'react'
import { cn } from '../lib/utils'
import { Progress as ProgressPrimitive } from '@base-ui/react/progress'

// oxlint-disable-next-line no-warning-comments -- TODO preserved per repo policy
// TODO: add loading indicator (with shapes).

export type ProgressSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl'
export type ProgressVariant = 'linear' | 'circular'

type ProgressContextValue = {
  variant: ProgressVariant
  size: ProgressSize
  value: number | null
  min: number
  max: number
  isIndeterminate: boolean
}

const ProgressContext = createContext<ProgressContextValue | null>(null)
function useProgressContext() {
  const ctx = useContext(ProgressContext)
  if (!ctx)
    throw new Error('Progress components must be used within <Progress>')

  return ctx
}

interface ProgressProps extends Omit<ProgressPrimitive.Root.Props, 'value'> {
  variant?: ProgressVariant
  size?: ProgressSize
  value?: number | null // overrode the value to include undefined for convenience
}

function Progress({
  className,
  children,
  variant = 'linear',
  size = 'default',
  value,
  ...props
}: ProgressProps) {
  const normalizedValue = value ?? null
  const min = props.min ?? 0
  const max = props.max ?? 100
  const isIndeterminate = normalizedValue === null

  const rootClassName =
    variant === 'circular'
      ? 'relative inline-flex justify-center items-center'
      : 'flex flex-col gap-2'

  return (
    <ProgressContext.Provider
      value={useMemo(
        () => ({
          variant,
          size,
          value: normalizedValue,
          min,
          max,
          isIndeterminate,
        }),
        [variant, size, normalizedValue, min, max, isIndeterminate],
      )}
    >
      <ProgressPrimitive.Root
        className={cn(rootClassName, className)}
        data-slot="progress"
        value={normalizedValue}
        // Phrasing content (spans, like MUI LinearProgress): a progress may sit inside text, e.g. an inline
        // AudioPlayer loading in a <p>; a <div> there is invalid HTML. Every part sets its display explicitly.
        render={<span />}
        {...props}
      >
        {children ?? (
          <ProgressTrack>
            <ProgressIndicator />
          </ProgressTrack>
        )}
      </ProgressPrimitive.Root>
    </ProgressContext.Provider>
  )
}

function ProgressTrack({
  className,
  children,
  ...props
}: ProgressPrimitive.Track.Props) {
  const { variant, size, value, min, max, isIndeterminate } =
    useProgressContext()

  // Circular track - indeterminate
  if (variant === 'circular' && isIndeterminate) {
    const { radius, strokeWidth, svgSize, center } = getCircularGeometry(size)

    return (
      <ProgressPrimitive.Track
        className={className}
        data-slot="progress-track"
        {...props}
        render={(trackProps) => (
          <svg
            className="animate-progress-circular-rotate overflow-visible"
            width={svgSize}
            height={svgSize}
            style={circularCssVars(size)}
          >
            <circle
              {...trackProps}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              opacity={0.2}
              className="animate-progress-circular-unfilled -rotate-90"
              style={{ transformOrigin: `${center}px ${center}px` }}
            />
            {children}
          </svg>
        )}
      />
    )
  }

  // Circular track - determinate
  if (variant === 'circular') {
    const { radius, strokeWidth, circumference, svgSize, center, gapSize } =
      getCircularGeometry(size)

    const progressProportion = calcProgressProportion(value, min, max)
    const filledArcLength = circumference * progressProportion
    const isZeroProgress = progressProportion === 0
    const unfilledArcLength = isZeroProgress
      ? circumference
      : Math.max(0, circumference - filledArcLength - gapSize * 2)
    const unfilledOffset = isZeroProgress ? 0 : filledArcLength + gapSize

    const fadeThreshold = circumference * 0.05
    const unfilledOpacity = Math.min(
      0.1,
      0.1 * (unfilledArcLength / fadeThreshold),
    )

    // <svg> has overflow-visible to allow anti-aliasing to extend beyond SVG bounds to prevent clipping
    return (
      <ProgressPrimitive.Track
        className={className}
        data-slot="progress-track"
        {...props}
        render={(trackProps) => (
          <svg className="overflow-visible" width={svgSize} height={svgSize}>
            {unfilledArcLength > 0 && (
              <circle
                {...trackProps}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeDasharray={`${unfilledArcLength} ${circumference - unfilledArcLength}`}
                strokeDashoffset={-unfilledOffset}
                strokeLinecap="round"
                opacity={unfilledOpacity}
                className="-rotate-90 transition-[stroke-dasharray,stroke-dashoffset,opacity] duration-500"
                style={{ transformOrigin: `${center}px ${center}px` }}
              />
            )}
            {children}
          </svg>
        )}
      />
    )
  }

  // Linear track - indeterminate
  if (isIndeterminate) {
    return (
      <ProgressPrimitive.Track
        className={cn(
          'relative block overflow-hidden rounded-full',
          LINEAR_SIZE_CONFIG[size],
          className,
        )}
        data-slot="progress-track"
        render={<span />}
        {...props}
      >
        <span className="animate-progress-linear-before absolute inset-0 origin-left rounded-full bg-current opacity-20" />
        {children}
        <span className="animate-progress-linear-after absolute inset-0 origin-right rounded-full bg-current opacity-20" />
      </ProgressPrimitive.Track>
    )
  }

  // Linear track - determinate
  return (
    <ProgressPrimitive.Track
      className={cn(
        'flex items-center gap-1 rounded-full',
        LINEAR_SIZE_CONFIG[size],
        className,
      )}
      data-slot="progress-track"
      render={<span />}
      {...props}
    >
      {children}
      <span className="h-full flex-1 rounded-full bg-current opacity-20" />
    </ProgressPrimitive.Track>
  )
}

function ProgressIndicator({
  className,
  ...props
}: ProgressPrimitive.Indicator.Props) {
  const { variant, size, value, min, max, isIndeterminate } =
    useProgressContext()

  // Circular indicator - indeterminate
  if (variant === 'circular' && isIndeterminate) {
    const { radius, strokeWidth, center } = getCircularGeometry(size)

    return (
      <ProgressPrimitive.Indicator
        className={className}
        data-slot="progress-indicator"
        {...props}
        render={(indicatorProps) => (
          <circle
            {...indicatorProps}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            className={cn(
              'animate-progress-circular-filled -rotate-90',
              className,
            )}
            style={{ transformOrigin: `${center}px ${center}px` }}
          />
        )}
      />
    )
  }

  // Circular indicator - determinate
  if (variant === 'circular') {
    const { radius, strokeWidth, circumference, center } =
      getCircularGeometry(size)

    const progressProportion = calcProgressProportion(value, min, max)
    const filledArcLength = circumference * progressProportion

    return (
      <ProgressPrimitive.Indicator
        className={className}
        data-slot="progress-indicator"
        {...props}
        render={(indicatorProps) => (
          <circle
            {...indicatorProps}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeDasharray={`${filledArcLength} ${circumference - filledArcLength}`}
            strokeDashoffset={0}
            strokeLinecap="round"
            className={cn(
              '-rotate-90 transition-[stroke-dasharray] duration-500',
              className,
            )}
            style={{ transformOrigin: `${center}px ${center}px` }}
          />
        )}
      />
    )
  }

  // Linear indicator - indeterminate
  if (isIndeterminate) {
    return (
      <ProgressPrimitive.Indicator
        className={cn(
          'animate-progress-linear-indicator absolute inset-0 origin-left rounded-full bg-current',
          className,
        )}
        data-slot="progress-indicator"
        render={<span />}
        {...props}
      />
    )
  }

  // Linear indicator - determinate
  return (
    <ProgressPrimitive.Indicator
      className={cn(
        'rounded-full bg-current transition-[width] duration-500',
        className,
      )}
      data-slot="progress-indicator"
      render={<span />}
      {...props}
    />
  )
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
  return (
    <ProgressPrimitive.Label
      className={cn('text-sm font-medium select-none', className)}
      data-slot="progress-label"
      {...props}
    />
  )
}

function ProgressValue({ className, ...props }: ProgressPrimitive.Value.Props) {
  const { variant } = useProgressContext()

  if (variant === 'circular') {
    return (
      <span className="@container absolute inset-0 flex items-center justify-center">
        <ProgressPrimitive.Value
          className={cn('text-[18cqi] tabular-nums select-none', className)}
          data-slot="progress-value"
          {...props}
        />
      </span>
    )
  }

  return (
    <ProgressPrimitive.Value
      className={cn('text-sm tabular-nums select-none', className)}
      data-slot="progress-value"
      {...props}
    />
  )
}

const LINEAR_SIZE_CONFIG = {
  xs: 'h-0.5',
  sm: 'h-1',
  default: 'h-1.5',
  lg: 'h-2',
  xl: 'h-3',
} as const

/** Calculate progress proportion with proper min/max handling and clamping */
function calcProgressProportion(
  value: number | null,
  min: number,
  max: number,
): number {
  if (value === null) return 0

  const clampedValue = Math.max(min, Math.min(max, value))

  const range = max - min
  if (range <= 0) return 0
  return (clampedValue - min) / range
}

const CIRCULAR_GAP_RATIO = 0.1 // gap as percentage of circumference
const CIRCULAR_MIN_ARC_RATIO = 0.02 // minimum arc length as percentage of circumference

// svgSize = 2*radius + strokeWidth; ratio strokeWidth/radius ≈ 0.4
const CIRCULAR_SIZE_CONFIG = {
  xs: { radius: 7, strokeWidth: 3 }, // svgSize=17
  sm: { radius: 10, strokeWidth: 4 }, // svgSize=24
  default: { radius: 16, strokeWidth: 6 }, // svgSize=38
  lg: { radius: 22, strokeWidth: 8 }, // svgSize=52
  xl: { radius: 30, strokeWidth: 12 }, // svgSize=72
} as const

function getCircularGeometry(size: ProgressSize) {
  const { radius, strokeWidth } = CIRCULAR_SIZE_CONFIG[size]
  const circumference = 2 * Math.PI * radius
  const svgSize = 2 * radius + strokeWidth
  const center = svgSize / 2
  const gapSize = circumference * CIRCULAR_GAP_RATIO
  const minArcLen = circumference * CIRCULAR_MIN_ARC_RATIO
  const maxArcLen = circumference / 2
  return {
    radius,
    strokeWidth,
    circumference,
    svgSize,
    center,
    gapSize,
    minArcLen,
    maxArcLen,
  }
}

function circularCssVars(size: ProgressSize): CSSProperties {
  const { circumference, gapSize, minArcLen, maxArcLen } =
    getCircularGeometry(size)
  return {
    '--prog-c': circumference,
    '--prog-min': minArcLen,
    '--prog-max': maxArcLen,
    '--prog-gap': gapSize,
  } as CSSProperties
}

export {
  Progress,
  ProgressLabel,
  ProgressTrack,
  ProgressIndicator,
  ProgressValue,
}
